import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { apiPost, apiDelete } from '../utils/apiUtils';
import './InteractiveScenesCategory.css';

const InteractiveScenesCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  // Use interactive scenes from novel prop
  const interactiveScenes = novel?.interactive_scenes || [];

  const handleCreateScene = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const tag = formData.get('tag')?.trim() || '';

    if (!name) {
      setError('Название сцены обязательно');
      setLoading(false);
      return;
    }

    try {
      const requestData = { name, tag };
      await apiPost(
        isProduction ? API_URLS.PRODUCTION_INTERACTIVE_SCENES(novelId) : API_URLS.BUILDING_INTERACTIVE_SCENES(novelId),
        requestData,
        { headers: { 'Content-Type': 'application/json' } },
        true,
        'Интерактивная сцена создана успешно!'
      );

      setShowCreateForm(false);

      // Refresh novel data to ensure consistency across all components
      await refreshNovel();
    } catch (err) {
      // Error is already shown by toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScene = async (sceneId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту интерактивную сцену?')) {
      return;
    }

    try {
      setLoading(true);
      await apiDelete(
        isProduction ? API_URLS.PRODUCTION_INTERACTIVE_SCENE(novelId, sceneId) : API_URLS.BUILDING_INTERACTIVE_SCENE(novelId, sceneId),
        {},
        true,
        'Интерактивная сцена удалена успешно!'
      );

      // Refresh novel data to ensure consistency across all components
      await refreshNovel();
    } catch (err) {
      // Error is already shown by toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleEditScene = (sceneId) => {
    // Navigate to the interactive scene detail page
    const basePath = isProduction ? 'production_change' : 'building';
    navigate(`/${basePath}/${novelId}/interactive_scene/${sceneId}`);
  };

  // Get unique tags from scenes (split by spaces)
  const allTags = [...new Set(interactiveScenes
    .filter(scene => scene && scene.tag)
    .flatMap(scene => scene.tag.split(/\s+/).filter(tag => tag.trim()))
  )];

  // Filter scenes based on search term and selected tags
  const filtered = interactiveScenes.filter(scene => {
    const matchesSearch = scene && scene.name && scene.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTags = selectedTags.length === 0 ||
      (scene.tag && selectedTags.every(selectedTag =>
        scene.tag.split(/\s+/).includes(selectedTag)
      ));
    return matchesSearch && matchesTags;
  });

  // Reset page on filter change
  useEffect(() => { setPage(0); }, [searchTerm, selectedTags]);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="isc-wrap">
      {/* Description */}
      <div className="isc-desc">
        <p>Интерактивные сцены - это места, где пользователи взаимодействуют с персонажами через диалоги и действия.</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="isc-error">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="isc-overlay">
          <div className="isc-modal">
            <h3 className="isc-modal-title">Добавить интерактивную сцену</h3>
            <form onSubmit={handleCreateScene}>
              <div className="isc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название сцены"
                  required
                  disabled={loading}
                  className="isc-input"
                  autoFocus
                />
              </div>
              <div className="isc-form-group">
                <input
                  type="text"
                  name="tag"
                  placeholder="Тег (необязательно)"
                  disabled={loading}
                  className="isc-input"
                />
              </div>

              <div className="isc-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="isc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setError(null);
                  }}
                  disabled={loading}
                  className="isc-btn-cancel"
                >
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Filter */}
      {allTags.length > 0 && (
        <div className="isc-tags">
          <button
            className={`isc-tag ${selectedTags.length === 0 ? 'active' : ''}`}
            onClick={() => setSelectedTags([])}
          >
            Все
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              className={`isc-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter(t => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar: Search + Add Button */}
      <div className="isc-toolbar">
        <input
          type="text"
          placeholder="Поиск сцен..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="isc-search"
        />
        {(searchTerm || selectedTags.length > 0) && (
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontFamily: "'Raleway', sans-serif" }}>
            {filtered.length} сцен найдено
          </span>
        )}
        <button
          className="isc-add-btn"
          onClick={() => { setShowCreateForm(true); setError(null); }}
          disabled={loading}
        >
          + Добавить интерактивную сцену
        </button>
      </div>

      {/* Interactive Scenes List */}
      <div className="isc-list">
        {paged.length > 0 ? (
          paged.map((scene) => (
            <div key={scene.id} className="isc-card">
              <div className="isc-card-head">
                <h4 className="isc-card-name">{scene.name}</h4>
                <div className="isc-card-actions">
                  {scene.tag && (
                    <span className="isc-card-tag">{scene.tag}</span>
                  )}
                  <button
                    className="isc-btn-edit"
                    onClick={() => handleEditScene(scene.id)}
                    disabled={loading}
                  >
                    Редактировать
                  </button>
                  <button
                    className="isc-btn-delete"
                    onClick={() => handleDeleteScene(scene.id)}
                    disabled={loading}
                  >
                    Удалить
                  </button>
                </div>
              </div>
              {scene.description && (
                <p className="isc-card-desc">{scene.description}</p>
              )}
            </div>
          ))
        ) : searchTerm ? (
          <div className="isc-empty">
            <p>Сцены не найдены по запросу "{searchTerm}"</p>
          </div>
        ) : (
          <div className="isc-empty">
            <p>Интерактивных сцен пока нет. Создайте свою первую сцену!</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="isc-pagination">
          <button className="isc-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Назад</button>
          <span>Страница {page + 1} из {totalPages}</span>
          <button className="isc-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Вперёд →</button>
        </div>
      )}
    </div>
  );
};

export default InteractiveScenesCategory;
